import { textWidth, fitText } from './boxLabelLayout';

// The Sender Label (issue #451, vis_order_fulfilment.md "Labels"): one per
// Parcel, on the outside of the box, showing who sent it and where to return
// it. In the printer's own dots, as boxLabelLayout.js is, and drawn and sent
// the same way (BoxLabelPreview, toZpl).
//
// The logo heads the label, centred. Below comes "From:", with the order number
// level with it at the right, then, indented: the sender's name, the return
// address, then "Ph:" and the phone. "Parcel 1 of 2" sits at the right of the
// last line on a Shipment of more than one. A detail the sender hasn't recorded
// is left out and the rest closes up.
//
// Sizes are set, not worked out from the room left: on a 69 x 48 mm label the
// name is 11 pt, other text 10 pt and the logo 21 mm wide, and other label
// sizes scale from those. Only when it all can't fit does the text get smaller.
// Past the size's smallest readable text, details give way one at a time:
// phone, logo, name. The return address is last, cut short only when it alone
// can't fit.

const MM_PER_INCH = 25.4;
const PT_PER_INCH = 72;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const lineOf = (h) => Math.round(h * 1.2);

// The grey a logo's pixel prints as on white paper; transparent counts as
// paper. logo: { width, height, data } (RGBA, as a canvas gives it).
function greyOnPaper(logo, i) {
  const alpha = logo.data[i + 3] / 255;
  const grey = 0.299 * logo.data[i] + 0.587 * logo.data[i + 1] + 0.114 * logo.data[i + 2];
  return grey * alpha + 255 * (1 - alpha);
}

// The label printer prints black only, so the logo becomes whole dots: each
// one dark when the part of the logo under it is.
export function logoDots(logo, cols, rows) {
  const cells = new Array(cols * rows).fill(0);
  for (let r = 0; r < rows; r += 1) {
    const y0 = Math.floor((r * logo.height) / rows);
    const y1 = Math.max(y0 + 1, Math.floor(((r + 1) * logo.height) / rows));
    for (let c = 0; c < cols; c += 1) {
      const x0 = Math.floor((c * logo.width) / cols);
      const x1 = Math.max(x0 + 1, Math.floor(((c + 1) * logo.width) / cols));
      let sum = 0;
      for (let y = y0; y < y1; y += 1) {
        for (let x = x0; x < x1; x += 1) sum += greyOnPaper(logo, (y * logo.width + x) * 4);
      }
      if (sum / ((x1 - x0) * (y1 - y0)) < 128) cells[r * cols + c] = 1;
    }
  }
  return { cols, rows, cells };
}

// The logo cropped to its artwork, so blank space in the file doesn't shrink
// it on the label. Null when there's nothing that would print.
export function trimLogo(logo) {
  let left = logo.width;
  let right = -1;
  let top = logo.height;
  let bottom = -1;
  for (let y = 0; y < logo.height; y += 1) {
    for (let x = 0; x < logo.width; x += 1) {
      if (greyOnPaper(logo, (y * logo.width + x) * 4) < 128) {
        left = Math.min(left, x);
        right = Math.max(right, x);
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
      }
    }
  }
  if (right < 0) return null;
  const width = right - left + 1;
  const height = bottom - top + 1;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    const start = ((y + top) * logo.width + left) * 4;
    data.set(logo.data.subarray(start, start + width * 4), y * width * 4);
  }
  return { width, height, data };
}

// The text as lines no wider than `maxW` dots, broken between words. `cut`
// says a single word was too wide and had to be cut short.
function wrap(text, h, maxW) {
  const lines = [];
  let cut = false;
  let line = '';
  String(text).split(/\s+/).filter(Boolean).forEach((word) => {
    const joined = line ? `${line} ${word}` : word;
    if (textWidth(joined, h) <= maxW) {
      line = joined;
      return;
    }
    if (line) lines.push(line);
    line = word;
    if (textWidth(word, h) > maxW) {
      line = fitText(word, h, maxW);
      cut = true;
    }
  });
  if (line) lines.push(line);
  return { lines, cut };
}

// What gives way, in order, as far as the sender has it to give.
const GIVES_WAY = ['phone', 'logo', 'name'];

// The logo's set width as a part of the label's, and the most of its height a
// tall logo may take. Text: 10 pt on a label 48 mm on its short side.
const LOGO_WIDTH = 0.3025;
const LOGO_MOST_HEIGHT = 0.22;
const TEXT_PT_PER_MM = 10 / 48;
const NAME_LARGER = 1.1;

// sender: { name, address_lines, phone }
// orderNumber: the Shipment's order, as staff would look it up.
// parcels: how many Parcels the Shipment has; one label each.
// logo: { width, height, data }, or null.
// size: { width_mm, height_mm }
// Returns { labels: [layout, ...], left_off: ['phone', ...] }, each layout
// as boxLabelLayout's.
export function layoutSenderLabels({ sender, orderNumber, parcels, logo, size, dpi }) {
  const perMm = dpi / MM_PER_INCH;
  const mm = (v) => Math.round(v * perMm);
  const pt = (v) => Math.round((v / PT_PER_INCH) * dpi);
  const widthMm = Number(size.width_mm);
  const heightMm = Number(size.height_mm);
  const W = mm(widthMm);
  const H = mm(heightMm);
  const short = Math.min(widthMm, heightMm);
  const maxPt = clamp(short * TEXT_PT_PER_MM, 6, 16);
  const minPt = clamp(short * 0.12, 5.5, 7);
  const pad = mm(Math.max(1.5, short * 0.05));
  const gap = mm(Math.max(1.5, short * 0.04));
  const indent = mm(short * 0.06);
  const innerW = W - 2 * pad;
  const innerH = H - 2 * pad;
  const textX = pad + indent;
  const textW = W - pad - textX;
  const counted = parcels > 1;

  const has = {
    phone: !!sender.phone,
    logo: !!logo,
    name: !!sender.name,
  };

  // Room for the widest count on this Shipment.
  const countWidth = (h) => Math.min(innerW, Math.ceil(textWidth(`Parcel ${parcels} of ${parcels}`, h)));

  // The label at this text size with these details, or null if it doesn't
  // fit. `force` keeps it inside the label regardless, for when only the
  // return address is left: lines past the bottom go, and the last is cut.
  const attempt = (show, p, force = false) => {
    const h = pt(p);
    const nameH = pt(p * NAME_LARGER);
    const boldBy = Math.max(1, Math.round(nameH * 0.04));
    const contactGap = Math.round(h * 0.45);

    const rows = [{ h, text: 'From:', x: pad, w: innerW, line: lineOf(h), gap: 0 }];
    let cut = false;
    const wrapped = (value, rowH, extra) => {
      const { lines, cut: wasCut } = wrap(value, rowH, textW - (extra.boldBy || 0));
      cut = cut || wasCut;
      lines.forEach((text) => rows.push({ h: rowH, text, x: textX, w: textW, line: lineOf(rowH), gap: 0, ...extra }));
    };
    if (show.name) wrapped(sender.name, nameH, { boldBy });
    (sender.address_lines || []).forEach((line) => wrapped(line, h, {}));
    if (show.phone) wrapped(`Ph: ${sender.phone}`, h, { gap: contactGap });

    let used = rows.reduce((sum, row) => sum + row.gap + row.line, 0);
    if (!force && (cut || used > innerH)) return null;
    while (used > innerH && rows.length > 1) {
      const dropped = rows.pop();
      used -= dropped.gap + dropped.line;
      const last = rows[rows.length - 1];
      last.text = `${last.text}...`;
    }

    // The count shares the last line, which keeps clear of it.
    if (counted && rows.length > 1) {
      const last = rows[rows.length - 1];
      last.w -= countWidth(h) + gap;
      if (!force && textWidth(last.text, last.h) > last.w) return null;
    }

    const items = [];
    let y = pad;

    // The logo heads the label, centred, at its set size. Without room for
    // that, it can't fit at this text size.
    if (show.logo) {
      const scale = Math.min((W * LOGO_WIDTH) / logo.width, (H * LOGO_MOST_HEIGHT) / logo.height);
      const cols = Math.max(1, Math.floor(logo.width * scale));
      const logoRows = Math.max(1, Math.floor(logo.height * scale));
      if (used + logoRows + gap > innerH) return null;
      items.push({ type: 'matrix', role: 'logo', x: Math.floor((W - cols) / 2), y: pad, module: 1, matrix: { cols, rows: logoRows, cells: null } });
      y += logoRows + gap;
      used += logoRows + gap;
    }

    // Spare height opens the lines up evenly, within reason.
    const spread = rows.length > 1 ? Math.min((innerH - used) / (rows.length - 1), h * 0.5) : 0;
    // Under a logo, the lines sit down at the bottom margin.
    if (show.logo) y += innerH - used - spread * (rows.length - 1);
    rows.forEach((row, i) => {
      y += row.gap + (i > 0 ? spread : 0);
      row.y = Math.round(y);
      y += row.line;
      const text = fitText(row.text, row.h, row.w);
      items.push({ type: 'text', x: row.x, y: row.y, h: row.h, w: row.w - (row.boldBy || 0), text, align: 'left' });
      // The printer has one weight of font, so bold is printed twice, a dot
      // or so apart.
      if (row.boldBy) items.push({ type: 'text', x: row.x + row.boldBy, y: row.y, h: row.h, w: row.w - row.boldBy, text, align: 'left', echo: true });
    });
    if (orderNumber) {
      const room = innerW - textWidth('From:', h) - gap;
      const orderW = Math.min(room, Math.ceil(textWidth(orderNumber, h)));
      items.push({ type: 'text', x: W - pad - orderW, y: rows[0].y, h, w: orderW, text: fitText(orderNumber, h, orderW), align: 'right' });
    }
    const last = rows[rows.length - 1];
    return { items, h, countY: rows.length > 1 ? last.y : last.y + last.line };
  };

  // Largest text first, then each detail in turn gives way.
  const show = { ...has };
  const leftOff = [];
  let fitted = null;
  const shrink = () => {
    for (let p = maxPt; p >= minPt - 0.01; p -= 0.5) {
      fitted = attempt(show, p);
      if (fitted) return;
    }
  };
  shrink();
  GIVES_WAY.forEach((detail) => {
    if (fitted || !has[detail]) return;
    show[detail] = false;
    leftOff.push(detail);
    shrink();
  });
  if (!fitted) fitted = attempt(show, minPt, true);

  const items = fitted.items;
  const logoItem = items.find((i) => i.role === 'logo');
  if (logoItem) logoItem.matrix = logoDots(logo, logoItem.matrix.cols, logoItem.matrix.rows);

  const countW = countWidth(fitted.h);
  const labels = Array.from({ length: Math.max(parcels, 0) }, (_, i) => ({
    dpi,
    width: W,
    height: H,
    overflow: false,
    items: counted
      ? [...items, { type: 'text', x: W - pad - countW, y: fitted.countY, h: fitted.h, w: countW, text: fitText(`Parcel ${i + 1} of ${parcels}`, fitted.h, countW), align: 'right' }]
      : items,
  }));
  return { labels, left_off: leftOff };
}

const LOGO_WORKING_SIDE = 800;

// The logo's pixels, drawn once at a working size so a huge or scalable (SVG)
// logo costs the same as any other. Null when it can't be read: the label
// then closes up, as it does for a sender with no logo.
export async function readLogoPixels(blob) {
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const naturalW = image.naturalWidth || LOGO_WORKING_SIDE;
    const naturalH = image.naturalHeight || LOGO_WORKING_SIDE;
    const scale = LOGO_WORKING_SIDE / Math.max(naturalW, naturalH);
    const width = Math.max(1, Math.round(naturalW * scale));
    const height = Math.max(1, Math.round(naturalH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, width, height);
    return trimLogo({ width, height, data: ctx.getImageData(0, 0, width, height).data });
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
